import { useStore } from '@/hooks/useStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ITEMS_CONST, ROUTES } from '@/configs/constants';
import { toastify } from '@/utils/toastify';
import Header from '@/components/layout/Header';
import { flowResult } from 'mobx';
import imgSend from '@/assets/images/pngs/NavigationArrow.png';
import { Button } from '@/components/common/Button';
import { de } from 'date-fns/locale';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import Title from '@/components/common/Title';
import { cn } from '@/utils/utils';
import { useForm } from 'react-hook-form';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import UploadImage from '@/components/common/UploadImage';
import RadioGroupComponent from '@/components/common/RadioGroupHtml';

export default observer(function EditItemRegistered() {
    // store
    const {
        itemsStore: { getDetailItemRegistered, detailItemRegistered, updateRegisteredItemById, uploadFile }
    } = useStore();
    const { t } = useTranslation();
    const location = useLocation();
    const { itemId } = location.state || {};
    const navigate = useNavigate();
    const goBack = () => {
        navigate(ROUTES.collectefItems.href);
    };
    const [fileSelected, setFileSelected] = useState<File | undefined>(undefined);
    const validateSchema = yup.object().shape({
        fileId: yup.number(),
        title: yup.string().trim().required('form_error_validate.required').max(255, 'form_error_validate.max_255'),
        description: yup.string().trim().required('form_error_validate.required').max(1000, 'form_error_validate.max_1000'),
        status: yup.number()
    });
    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            fileId: undefined,
            title: detailItemRegistered?.item,
            description: detailItemRegistered?.description,
            status: detailItemRegistered?.status
        }
    });

    useEffect(() => {
        if (detailItemRegistered && detailItemRegistered.id) {
            form.setValue('title', detailItemRegistered.item || '');
            form.setValue('description', detailItemRegistered.description || '');
        }
    }, [detailItemRegistered]);

    useEffect(() => {
        getDetailItemRegistered(itemId);
    }, [itemId]);

    const getActivityText = (activity: number | undefined) => {
        switch (activity) {
            case ITEMS_CONST.ACTIVITY.PUBLISHED__USED:
                return t('itemActivity.used');
            case ITEMS_CONST.ACTIVITY.PRIVATE__WITHDRAWAL:
                return t('itemActivity.withdrawal');
            case ITEMS_CONST.ACTIVITY.PUBLISHED__GET_IT:
                return t('itemActivity.getIt');
            case ITEMS_CONST.ACTIVITY.PUBLISHED__RESTORE:
                return t('itemActivity.restore');
            case ITEMS_CONST.ACTIVITY.PRIVATE__REPORT:
                return t('itemActivity.report');
            case ITEMS_CONST.ACTIVITY.PUBLISHED__ON_MAP:
                return t('itemActivity.onMap');
            case ITEMS_CONST.ACTIVITY.PRIVATE__EXPIRED:
                return t('itemActivity.expired');
            default:
                return '';
        }
    };
    const getBgColorActivity = (activity: number | undefined) => {
        switch (activity) {
            case ITEMS_CONST.ACTIVITY.PUBLISHED__USED:
                return 'bg-gradient-to-r from-[#35EA8B] to-[#43F4BF]';
            case ITEMS_CONST.ACTIVITY.PRIVATE__WITHDRAWAL:
                return 'bg-gradient-to-r from-[#EA355D] to-[#F44343]';
            case ITEMS_CONST.ACTIVITY.PUBLISHED__GET_IT:
                return 'bg-gradient-to-r from-[#356FEA] to-[#F443AE]';
            case ITEMS_CONST.ACTIVITY.PUBLISHED__RESTORE:
                return 'bg-gradient-to-r from-[#FFAD00] to-[#E5D541]';
            case ITEMS_CONST.ACTIVITY.PRIVATE__REPORT:
                return 'bg-gradient-to-r from-[#FF200E] to-[#F44343]';
            case ITEMS_CONST.ACTIVITY.PUBLISHED__ON_MAP:
                return 'bg-gradient-to-r from-[#35EA8B] to-[#43F4BF]';
            case ITEMS_CONST.ACTIVITY.PRIVATE__EXPIRED:
                return 'bg-gradient-to-r from-[#FF200E] to-[#F44343]';
            default:
                return '';
        }
    };

    const getBgType = (type: number | undefined) => {
        switch (type) {
            case ITEMS_CONST.ITEM_TYPE.COUPON:
                return 'bg-gradient-to-r from-[#EA4335] to-[#F44343]';
            case ITEMS_CONST.ITEM_TYPE.NOT_SET:
                return 'bg-gradient-to-r from-[#35EA8B] to-[#43F4BF]';
            default:
                return '';
        }
    };

    const handleSubmitData = async (form: FormData) => {
        if (fileSelected) {
            const res = await flowResult(uploadFile(fileSelected));
            if (res) {
                const resUpdate = await flowResult(updateRegisteredItemById({ ...form, id: detailItemRegistered?.id, file_id: res }));
                if (resUpdate) {
                    navigate(ROUTES.collectefItems.href);
                }
            }
        } else {
            const res = await flowResult(updateRegisteredItemById({ ...form, id: detailItemRegistered?.id }));
            if (res) {
                navigate(ROUTES.collectefItems.href);
            }
        }
    };
    const onUploadDone = (file) => {
        setFileSelected(file);
    };

    return (
        <div className='w-screen h-screen flex flex-col justify-start bg-white pt-[72px]'>
            <Header title={t('text.editItem')} goBackFunction={goBack} />
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmitData)} className='mx-4 h-[calc(100vh-72px-24px)] flex flex-col justify-between'>
                    <div className='py-6 overflow-y-scroll scrollbar-hide'>
                        <Title
                            title='アイテム情報'
                            titleClassname='text-[15px]  font-semibold text-[#333333]'
                        />
                        <Title
                            title={t('text.image')}
                            require
                        />
                        <UploadImage
                            path={detailItemRegistered?.file?.path}
                            alt={detailItemRegistered?.file?.filename}
                            className='mt-[6px]'
                            onSelectedImage={onUploadDone}
                            disabled={detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PUBLISHED__GET_IT || detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PUBLISHED__USED}
                        />
                        <FormField
                            control={form.control}
                            name='title'
                            render={({ field }) => (
                                <FormItem>
                                    <Title
                                        title='アイテム名'
                                        require
                                    />
                                    <FormControl>
                                        <Input
                                            disabled={detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PUBLISHED__GET_IT || detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PUBLISHED__USED}
                                            {...field}
                                            className='bg-bgInput border-bgMenubar'
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='description'
                            render={({ field }) => (
                                <FormItem>
                                    <Title
                                        title='アイテム説明'
                                        require
                                    />
                                    <FormControl>
                                        <Textarea
                                            maxLength={1000}
                                            disabled={detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PUBLISHED__GET_IT || detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PUBLISHED__USED}
                                            {...field}
                                            className='mt-[6px] bg-bgInput border-bgMenubar w-full h-[35vw]'
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <div className='flex justify-between content-center items-center'>
                            <Title
                                title='アイテム種別'
                            />
                            <p className={`text-white text-[15px] mt-4 px-3 py-1 rounded-[6px] ${getBgType(detailItemRegistered?.itemType)}`}>{detailItemRegistered?.itemType === ITEMS_CONST.ITEM_TYPE.COUPON ? 'クーポン' : '設定なし'}</p>
                        </div>

                        <div className='text-colorTxtDetailItem space-y-3 mt-3'>
                            {detailItemRegistered?.itemType === ITEMS_CONST.ITEM_TYPE.COUPON && (
                                <div className='flex justify-between content-center items-center'>
                                    <p>クーポンコード</p>
                                    <p className='font-semibold text-textAccountType'>{detailItemRegistered?.code}</p>
                                </div>
                            )}
                            {(detailItemRegistered?.status === ITEMS_CONST.STATUS.UN_PUBLISHED ||
                                detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PRIVATE__EXPIRED) ?
                                (
                                    <div>
                                        <p>ステータス</p>
                                        <RadioGroupComponent
                                            className='mt-3'
                                            items={[{ value: ITEMS_CONST.STATUS.UN_PUBLISHED.toString(), label: detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PRIVATE__EXPIRED ? t('statusToSet.private') : t('statusToSet.unPublict') }, { value: ITEMS_CONST.STATUS.PUBLISHED.toString(), label: t('statusToSet.publict') }
                                            ]}
                                            onSelect={(e) => {
                                                form.setValue('status', parseInt(e));
                                            }}
                                        />
                                    </div>
                                ) :
                                (
                                    <div className='flex justify-between content-center items-center'>
                                        <p>ステータス</p>
                                        <p className={cn('font-semibold', `${detailItemRegistered?.status === ITEMS_CONST.STATUS.PUBLISHED ? 'text-greenMain' : 'text-textRed'}`)}>{detailItemRegistered?.status === ITEMS_CONST.STATUS.PUBLISHED ? t('itemStatus.published') : t('itemStatus.private')}</p>
                                    </div>
                                )}

                            {detailItemRegistered?.status !== ITEMS_CONST.STATUS.UN_PUBLISHED && (
                                <div className={cn('flex justify-between content-center items-center')}>
                                    <p>アクティビティステータス</p>
                                    <p className={cn('px-4 py-1 rounded-[6px] text-white', getBgColorActivity(detailItemRegistered?.activity))}>{getActivityText(detailItemRegistered?.activity)}</p>
                                </div>
                            )}
                        </div>
                        {detailItemRegistered?.activity === ITEMS_CONST.ACTIVITY.PRIVATE__WITHDRAWAL && (
                            <div>
                                <Title title='理由' />
                                <Textarea value={detailItemRegistered?.reason} disabled className='mt-[6px] bg-bgInput border-bgMenubar w-full h-[35vw]' />
                            </div>

                        )}
                    </div>
                    {
                        detailItemRegistered?.activity !== ITEMS_CONST.ACTIVITY.PUBLISHED__GET_IT && detailItemRegistered?.activity !== ITEMS_CONST.ACTIVITY.PUBLISHED__USED && (
                            <Button
                                className='w-full '
                                variant='main'
                                type='submit'
                            >
                                {t('text.reNew')}
                            </Button>
                        )
                    }

                </form>
            </Form>
        </div>
    );
});
